/**
 * Tileable noise primitives — GLSL port of the old CPU `Noise.js`.
 *
 * Every function here is **periodic**: it takes a point already scaled into
 * lattice space (`uv * K`) plus the period `K`, and wraps its lattice lookups
 * with `mod`. That is what keeps the generated textures seamless — a visible
 * seam on a wall reads as amateur work instantly.
 *
 * Periods are `vec2` at the core (with scalar convenience overloads) so
 * anisotropic features — brushed metal, rain streaks, wood grain — can be
 * stretched along one axis and still wrap on both.
 *
 * Composition rule used throughout the surface library: anything added to the
 * sample point must itself be periodic over uv with period 1 (e.g.
 * `warp(uv,...) * K`). Rotation between octaves is deliberately absent — it
 * would destroy the lattice period.
 */

export const NOISE_GLSL = /* glsl */ `

float sat(float x) { return clamp(x, 0.0, 1.0); }
vec3  sat3(vec3 x) { return clamp(x, vec3(0.0), vec3(1.0)); }

// ---------------------------------------------------------------- hashing --

// Integer bit-mix, same construction as the old JS hash2(). Uses exact 32-bit
// uint ops so it is stable across drivers.
float n_hash(vec2 c, float seed) {
  uint h = uint(int(c.x)) * 374761393u
         + uint(int(c.y)) * 668265263u
         + uint(int(seed)) * 2246822519u;
  h = (h ^ (h >> 13u)) * 1274126177u;
  h = h ^ (h >> 16u);
  return float(h) * (1.0 / 4294967296.0);
}

vec2 n_hash2(vec2 c, float seed) {
  return vec2(n_hash(c, seed), n_hash(c, seed + 7919.0));
}

/** Per-cell random for lattice features (bricks, planks, tiles). Wraps on 'counts'. */
float cellRand(vec2 cell, vec2 counts, float seed) {
  return n_hash(mod(cell, counts), seed);
}

vec2 quintic(vec2 f) { return f * f * f * (f * (f * 6.0 - 15.0) + 10.0); }

// ------------------------------------------------------------ value noise --

float valueNoise(vec2 p, vec2 period, float seed) {
  vec2 i = floor(p);
  vec2 f = p - i;
  vec2 w = quintic(f);
  vec2 i0 = mod(i, period);
  vec2 i1 = mod(i0 + 1.0, period);
  float a = n_hash(vec2(i0.x, i0.y), seed);
  float b = n_hash(vec2(i1.x, i0.y), seed);
  float c = n_hash(vec2(i0.x, i1.y), seed);
  float d = n_hash(vec2(i1.x, i1.y), seed);
  return mix(mix(a, b, w.x), mix(c, d, w.x), w.y);
}

float valueNoise(vec2 p, float period, float seed) {
  return valueNoise(p, vec2(period), seed);
}

// --------------------------------------------------------- gradient noise --

// Periodic Perlin. One sin/cos per corner more than value noise, but with no
// axis-aligned blockiness — which matters once you stack 5+ octaves.
vec2 n_grad(vec2 cell, float seed) {
  float a = n_hash(cell, seed) * 6.28318530718;
  return vec2(cos(a), sin(a));
}

float gradNoise(vec2 p, vec2 period, float seed) {
  vec2 i = floor(p);
  vec2 f = p - i;
  vec2 w = quintic(f);
  vec2 i0 = mod(i, period);
  vec2 i1 = mod(i0 + 1.0, period);
  float a = dot(n_grad(vec2(i0.x, i0.y), seed), f - vec2(0.0, 0.0));
  float b = dot(n_grad(vec2(i1.x, i0.y), seed), f - vec2(1.0, 0.0));
  float c = dot(n_grad(vec2(i0.x, i1.y), seed), f - vec2(0.0, 1.0));
  float d = dot(n_grad(vec2(i1.x, i1.y), seed), f - vec2(1.0, 1.0));
  return mix(mix(a, b, w.x), mix(c, d, w.x), w.y) * 0.7071 + 0.5;
}

float gradNoise(vec2 p, float period, float seed) {
  return gradNoise(p, vec2(period), seed);
}

// ---------------------------------------------------------------- fractal --

float fbmGain(vec2 p, vec2 period, int octaves, float seed, float gain) {
  float sum = 0.0, amp = 1.0, norm = 0.0, f = 1.0;
  vec2 per = period;
  for (int i = 0; i < octaves; i++) {
    sum += valueNoise(p * f, per, seed + float(i) * 1013.0) * amp;
    norm += amp;
    amp *= gain;
    f *= 2.0;
    per *= 2.0;
  }
  return sum / norm;
}

float fbm(vec2 p, vec2 period, int octaves, float seed) {
  return fbmGain(p, period, octaves, seed, 0.5);
}
float fbm(vec2 p, float period, int octaves, float seed) {
  return fbmGain(p, vec2(period), octaves, seed, 0.5);
}

/** Perlin fbm — smoother and more organic. Macro tone, soft masks, warps. */
float fbmP(vec2 p, vec2 period, int octaves, float seed) {
  float sum = 0.0, amp = 1.0, norm = 0.0, f = 1.0;
  vec2 per = period;
  for (int i = 0; i < octaves; i++) {
    sum += gradNoise(p * f, per, seed + float(i) * 1013.0) * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2.0;
    per *= 2.0;
  }
  return sum / norm;
}
float fbmP(vec2 p, float period, int octaves, float seed) {
  return fbmP(p, vec2(period), octaves, seed);
}

/** Ridged fbm — sharp creases: cracked concrete, rust fronts, flaking paint. */
float ridged(vec2 p, vec2 period, int octaves, float seed) {
  float sum = 0.0, amp = 1.0, norm = 0.0, f = 1.0;
  vec2 per = period;
  for (int i = 0; i < octaves; i++) {
    float n = 1.0 - abs(valueNoise(p * f, per, seed + float(i) * 7717.0) * 2.0 - 1.0);
    sum += n * n * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2.0;
    per *= 2.0;
  }
  return sum / norm;
}
float ridged(vec2 p, float period, int octaves, float seed) {
  return ridged(p, vec2(period), octaves, seed);
}

// ------------------------------------------------------------- 1-D / axial --

/**
 * Periodic 1-D value noise. Wraps on 'period'.
 *
 * The reason this exists rather than "just use valueNoise with a squashed
 * period": a surface whose texture tile is *smaller* than the feature it has to
 * carry cannot use 2-D noise for that feature at all. Corrugated sheet tiles
 * every 0.51 m, so a 1 m rust region printed with 2-D fbm repeats twice a metre
 * and reads as camouflage. A *column* of rust, on the other hand, is constant
 * along v by construction, so it tiles invisibly however short the tile is and
 * runs the full height of the wall.
 */
float valueNoise1(float x, float period, float seed) {
  float i = floor(x);
  float f = x - i;
  float w = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float a = n_hash(vec2(mod(i, period), 0.0), seed);
  float b = n_hash(vec2(mod(i + 1.0, period), 0.0), seed);
  return mix(a, b, w);
}

/** 1-D fbm. Vertical streak masks, plank/rib selection, column-locked wear. */
float fbm1(float x, float period, int octaves, float seed) {
  float sum = 0.0, amp = 1.0, norm = 0.0, f = 1.0, per = period;
  for (int i = 0; i < octaves; i++) {
    sum += valueNoise1(x * f, per, seed + float(i) * 1013.0) * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2.0;
    per *= 2.0;
  }
  return sum / norm;
}

/**
 * Slow vertical drift that still tiles.
 *
 * gradNoise with a v-period of 1 shares one lattice row top and bottom, so the
 * value varies smoothly down the tile and arrives back where it started — a
 * gradient that never seams, however many times the tile repeats. Paired with
 * fbm1 it turns a hard column mask into one that wanders.
 */
float drift(vec2 uv, float cols, int octaves, float seed) {
  return fbmP(uv * vec2(cols, 1.0), vec2(cols, 1.0), octaves, seed);
}

// ----------------------------------------------------------------- worley --

/** Tiling cellular noise -> vec3(f1, f2, cellId). f2-f1 gives clean borders. */
vec3 worleyA(vec2 p, vec2 period, float seed, vec2 aniso) {
  vec2 ip = floor(p);
  float f1 = 1e9, f2 = 1e9, id = 0.0;
  for (int oy = -1; oy <= 1; oy++) {
    for (int ox = -1; ox <= 1; ox++) {
      vec2 c = ip + vec2(float(ox), float(oy));
      vec2 w = mod(c, period);
      vec2 pt = c + n_hash2(w, seed);
      float d = length((pt - p) * aniso);
      if (d < f1) { f2 = f1; f1 = d; id = n_hash(w, seed + 99.0); }
      else if (d < f2) { f2 = d; }
    }
  }
  return vec3(f1, f2, id);
}

vec3 worley(vec2 p, vec2 period, float seed) {
  return worleyA(p, period, seed, vec2(1.0));
}
vec3 worley(vec2 p, float period, float seed) {
  return worleyA(p, vec2(period), seed, vec2(1.0));
}

// ----------------------------------------------------------------- streak --

/** Directional smear — grime running down a wall, brushed metal, rust weeping. */
float streak(vec2 p, vec2 period, float seed, int len) {
  float sum = 0.0, w = 0.0;
  for (int i = 0; i < len; i++) {
    float a = 1.0 - float(i) / float(len);
    sum += valueNoise(vec2(p.x, p.y - float(i) * 0.35), period, seed) * a;
    w += a;
  }
  return sum / w;
}
float streak(vec2 p, float period, float seed, int len) {
  return streak(p, vec2(period), seed, len);
}

// ------------------------------------------------------------ domain warp --

/** Periodic domain warp in uv space (the result is still seamless). */
vec2 warp(vec2 uv, float period, float amount, float seed) {
  return uv + (vec2(fbmP(uv * period, period, 3, seed),
                    fbmP(uv * period, period, 3, seed + 313.0)) - 0.5) * amount;
}

// -------------------------------------------------------------- utilities --

float sharpen(float x, float k) { return sat((x - 0.5) * k + 0.5); }

/** Shared grime layer: dark streaks running downward over blotchy build-up. */
float grimeMask(vec2 uv, float seed) {
  float st = streak(uv * vec2(26.0, 20.0), vec2(26.0, 20.0), seed, 12);
  float bl = fbmP(uv * 6.0, 6.0, 4, seed + 51.0);
  float fine = fbm(uv * 40.0, 40.0, 3, seed + 7.0);
  return sat((st * 0.55 + bl * 0.35 + fine * 0.10 - 0.33) * 2.2);
}
`;
